import type { ReactNode } from "react";
import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { cn } from "@volca/ui/lib/utils";
import {
  AssigneeSelect,
  ConfidenceLabel,
  CopyPdfLinkButton,
  ManualJobMatchButton,
  PurchaseOrderMatchButton,
  QuickbooksBillPushButton,
  QuickbooksBillPushNote,
  QuickbooksBillStatus,
  ReviewHeaderLayout,
  ReviewHeaderStatus,
  StatusBadge,
  StFilingTargetLink,
} from "@/accounts-payable/common";
import type { ApInvoice } from "@/accounts-payable/types";
import {
  canManuallyMatchJob,
  formatApDate,
  invoiceStatusMeta,
  stFilingTarget,
  stPurchaseOrderUrl,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";

/**
 * The invoice review page's title block: the vendor, then the invoice's
 * number, date and total; the job match with its job and confidence, the
 * ServiceTitan PO the cost sits on, and the actions to change either.
 */
export const InvoiceReviewHeader = ({
  invoice,
  businessId,
  actions,
}: {
  invoice: ApInvoice;
  businessId: string | null;
  actions?: ReactNode;
}) => (
  <ReviewHeaderLayout
    title={invoice.vendor_name}
    facts={[
      invoice.invoice_number,
      formatApDate(invoice.invoice_date),
      invoice.total_amount_cents ? (
        <span
          className={cn("font-semibold tabular-nums", {
            "text-danger": invoice.total_amount_cents < 0,
            "text-ink": invoice.total_amount_cents >= 0,
          })}
        >
          {formatCentsOrDashIfZero(invoice.total_amount_cents)}
        </span>
      ) : null,
    ]}
    statuses={[
      // The match status reads with the job it is about and how sure it is.
      <ReviewHeaderStatus key="job">
        <StatusBadge meta={invoiceStatusMeta(invoice)} />
        {stFilingTarget(invoice) ? (
          <StFilingTargetLink document={invoice} className="ml-0" />
        ) : (
          canManuallyMatchJob(invoice) && <span>No job</span>
        )}
        {stFilingTarget(invoice) && invoice.job_match_confidence != null && (
          <ConfidenceLabel value={invoice.job_match_confidence} />
        )}
      </ReviewHeaderStatus>,
      // Its own status: the PO is a separate record from the job, and the one
      // the agent creates shows up here once it lands in ServiceTitan.
      invoice.st_po_external_id && (
        <ReviewHeaderStatus key="po">
          <ServiceTitanIconLink
            url={stPurchaseOrderUrl(invoice.st_po_external_id)}
            label={`Open purchase order #${invoice.st_po_external_id} in ServiceTitan`}
            className="ml-0 underline underline-offset-2"
          >
            PO #{invoice.st_po_external_id}
          </ServiceTitanIconLink>
        </ReviewHeaderStatus>
      ),
      invoice.quickbooks_bill && (
        <ReviewHeaderStatus key="quickbooks">
          <QuickbooksBillStatus bill={invoice.quickbooks_bill} />
        </ReviewHeaderStatus>
      ),
    ]}
    actions={
      <>
        <AssigneeSelect document={invoice} businessId={businessId} compact />
        {canManuallyMatchJob(invoice) && (
          <ManualJobMatchButton
            businessId={businessId}
            documentId={invoice.id}
            agentRunId={invoice.agent_run_id}
            label={
              stFilingTarget(invoice) ? "Change job or project" : undefined
            }
          />
        )}
        {canManuallyMatchJob(invoice) && (
          <PurchaseOrderMatchButton
            businessId={businessId}
            documentId={invoice.id}
            documentType="invoice"
            agentRunId={invoice.agent_run_id}
            hasJob={stFilingTarget(invoice) != null}
            label={invoice.st_po_external_id ? "Change PO" : undefined}
          />
        )}
        <QuickbooksBillPushButton invoice={invoice} businessId={businessId} />
        <CopyPdfLinkButton document={invoice} />
        {actions}
      </>
    }
  >
    <QuickbooksBillPushNote invoice={invoice} businessId={businessId} />
  </ReviewHeaderLayout>
);
